import { type ParentProps, createMemo } from "solid-js";
import { createStore } from "solid-js/store";
import { useWidgetApp } from "~/provider";
import { SpeechContext } from "./context";

const initialSpeakerValues = {
	queue: [],
	isListening: false,
} satisfies SpeechStoreValues;

interface SpeechStoreValues {
	queue: SpeechSynthesisUtterance[];
	isListening: boolean;
}

interface SpeechProviderProps {
	locale?: string;
}

export function SpeechProvider(props: ParentProps<SpeechProviderProps>) {
	const { locale } = useWidgetApp();
	const [speaker, setSpeaker] = createStore<SpeechStoreValues>({ ...initialSpeakerValues });

	const read = (text: string) => {
		const utterance = new SpeechSynthesisUtterance(text);

		utterance.rate = 1;
		utterance.lang = props.locale || locale;

		const voice = speechSynthesis.getVoices().find((voice) => voice.lang === utterance.lang);

		if (voice) {
			utterance.voice = voice;
		}

		utterance.onstart = () => {
			setSpeaker("isListening", true);
		};

		utterance.onend = () => {
			setSpeaker("queue", (arr) => arr.slice(1));

			if (speaker.queue.length > 0) {
				speechSynthesis.speak(speaker.queue[0]);
			} else {
				setSpeaker({ ...initialSpeakerValues });
				speechSynthesis.cancel();
			}
		};

		setSpeaker("queue", (arr) => [...arr, utterance]);
		speechSynthesis.speak(utterance);
	};

	const stop = () => {
		speechSynthesis.cancel();
		setSpeaker({ ...initialSpeakerValues });
	};

	const isListening = createMemo(() => speaker.isListening);

	return <SpeechContext.Provider value={{ isListening, read, stop }}>{props.children}</SpeechContext.Provider>;
}
